import { B as BaseComponentMixin, b as registerComponent } from './plugins-0d853d1f.mjs';
import { M as MatchMediaMixin } from './MatchMediaMixin-cccfda3c.mjs';
import { getOptions } from './config.mjs';
import { getValueByPath, removeElement } from './helpers.mjs';
import { defineComponent, withDirectives, openBlock, createElementBlock, normalizeClass, createCommentVNode, createVNode, Transition, withCtx, createElementVNode, renderSlot, vShow } from 'vue';

/**
 * A sidebar to use as left/right overlay or static
 * @displayName Sidebar
 * @style _sidebar.scss
 */
var script = defineComponent({
    name: 'OSidebar',
    mixins: [BaseComponentMixin, MatchMediaMixin],
    configField: 'sidebar',
    emits: ['update:open', 'close'],
    props: {
        /** To control the behaviour of the sidebar programmatically, use the v-model:open to make it two-way binding */
        open: Boolean,
        /**
        * Color of the sidebar, optional
        * @values primary, info, success, warning, danger, and any other custom color
        */
        variant: [String, Object],
        /** Show an overlay like modal */
        overlay: Boolean,
        /**
         * Skeleton position in relation to the window
         * @values fixed, absolute, static
         */
        position: {
            type: String,
            default: () => { return getValueByPath(getOptions(), 'sidebar.position', 'fixed'); },
            validator: (value) => {
                return [
                    'fixed',
                    'absolute',
                    'static'
                ].indexOf(value) >= 0;
            }
        },
        /** Show sidebar in fullheight */
        fullheight: Boolean,
        /** Show sidebar in fullwidth */
        fullwidth: Boolean,
        /** Show the sidebar on right */
        right: Boolean,
        /**
         * Custom layout on mobile
         * @values fullwidth, reduced, hidden
         */
        mobile: {
            type: String,
            validator: (value) => {
                return [
                    '',
                    'fullwidth',
                    'reduced',
                    'hidden'
                ].indexOf(value) >= 0;
            }
        },
        /** Show a small sidebar */
        reduce: Boolean,
        /** Expand sidebar on hover when reduced or mobile is reduce */
        expandOnHover: Boolean,
        /** Expand sidebar on hover with fixed position when reduced or mobile is reduce */
        expandOnHoverFixed: Boolean,
        /**
         * Sidebar cancel options
         * @values true, false, 'escape', 'outside'
         */
        canCancel: {
            type: [Array, Boolean],
            default: () => { return getValueByPath(getOptions(), 'sidebar.canCancel', ['escape', 'outside']); }
        },
        /**
         * Callback on cancel
         */
        onCancel: {
            type: Function,
            default: () => { }
        },
        scroll: {
            type: String,
            default: () => {
                return getValueByPath(getOptions(), 'sidebar.scroll', 'clip');
            },
            validator: (value) => {
                return [
                    'clip',
                    'keep'
                ].indexOf(value) >= 0;
            }
        },
        rootClass: [String, Function, Array],
        overlayClass: [String, Function, Array],
        contentClass: [String, Function, Array],
        fixedClass: [String, Function, Array],
        staticClass: [String, Function, Array],
        absoluteClass: [String, Function, Array],
        fullheightClass: [String, Function, Array],
        fullwidthClass: [String, Function, Array],
        rightClass: [String, Function, Array],
        reduceClass: [String, Function, Array],
        expandOnHoverClass: [String, Function, Array],
        expandOnHoverFixedClass: [String, Function, Array],
        variantClass: [String, Function, Array],
        mobileClass: [String, Function, Array],
        scrollClipClass: [String, Function, Array],
        noScrollClass: [String, Function, Array],
        hiddenClass: [String, Function, Array],
        visibleClass: [String, Function, Array]
    },
    data() {
        return {
            isOpen: this.open,
            transitionName: null,
            animating: true,
            savedScrollTop: null
        };
    },
    computed: {
        rootClasses() {
            return [
                this.computedClass('rootClass', 'o-side'),
                { [this.computedClass('mobileClass', 'o-side--mobile')]: this.isMatchMedia },
            ];
        },
        overlayClasses() {
            return [
                this.computedClass('overlayClass', 'o-side__overlay')
            ];
        },
        contentClasses() {
            return [
                this.computedClass('contentClass', 'o-side__content'),
                { [this.computedClass('variantClass', 'o-side__content--', this.variant)]: this.variant },
                { [this.computedClass('fixedClass', 'o-side__content--fixed')]: this.isFixed },
                { [this.computedClass('staticClass', 'o-side__content--static')]: this.isStatic },
                { [this.computedClass('absoluteClass', 'o-side__content--absolute')]: this.isAbsolute },
                { [this.computedClass('fullheightClass', 'o-side__content--fullheight')]: this.fullheight },
                { [this.computedClass('fullwidthClass', 'o-side__content--fullwidth')]: this.fullwidth || (this.mobile === 'fullwidth' && this.isMatchMedia) },
                { [this.computedClass('rightClass', 'o-side__content--right')]: this.right },
                { [this.computedClass('reduceClass', 'o-side__content--mini')]: this.reduce || (this.mobile === 'reduced' && this.isMatchMedia) },
                { [this.computedClass('expandOnHoverClass', 'o-side__content--mini-expand')]: (this.expandOnHover && this.mobile !== 'fullwidth') },
                { [this.computedClass('expandOnHoverFixedClass', 'o-side__content--expand-mini-hover-fixed')]: (this.expandOnHover && this.expandOnHoverFixed && this.mobile !== 'fullwidth') },
                { [this.computedClass('visibleClass', 'o-side__content--visible')]: this.isOpen },
                { [this.computedClass('hiddenClass', 'o-side__content--hidden')]: !this.isOpen },
            ];
        },
        scrollClass() {
            if (this.scroll === 'clip') {
                return this.computedClass('scrollClipClass', 'o-clipped');
            }
            return this.computedClass('noScrollClass', 'o-noscroll');
        },
        cancelOptions() {
            return typeof this.canCancel === 'boolean'
                ? this.canCancel
                    ? getValueByPath(getOptions(), 'sidebar.canCancel', ['escape', 'outside'])
                    : []
                : this.canCancel;
        },
        isStatic() {
            return this.position === 'static';
        },
        isFixed() {
            return this.position === 'fixed';
        },
        isAbsolute() {
            return this.position === 'absolute';
        },
        hideOnMobile() {
            return this.mobile === 'hidden' && this.isMatchMedia;
        }
    },
    watch: {
        open: {
            handler(value) {
                this.isOpen = value;
                if (this.overlay) {
                    this.handleScroll();
                }
                const open = this.right ? !value : value;
                this.transitionName = !open ? 'slide-prev' : 'slide-next';
            },
            immediate: true
        }
    },
    methods: {
        /**
        * Keypress event that is bound to the document.
        */
        keyPress({ key }) {
            if (this.isFixed) {
                if (this.isOpen && (key === 'Escape' || key === 'Esc'))
                    this.cancel('escape');
            }
        },
        /**
        * Close the Sidebar if canCancel and call the onCancel prop (function).
        */
        cancel(method) {
            if (this.cancelOptions.indexOf(method) < 0)
                return;
            if (this.isStatic)
                return;
            this.onCancel.apply(null, arguments);
            this.close();
        },
        /**
        * Call the onCancel prop (function) and emit events
        */
        close() {
            this.isOpen = false;
            this.$emit('close');
            this.$emit('update:open', false);
        },
        /**
         * Close fixed sidebar if clicked outside.
         */
        clickedOutside(event) {
            if (!this.isFixed || !this.isOpen || this.animating) {
                return;
            }
            if (!event.composedPath().includes(this.$refs.sidebarContent)) {
                this.cancel('outside');
            }
        },
        /**
        * Transition before-enter hook
        */
        beforeEnter() {
            this.animating = true;
        },
        /**
        * Transition after-leave hook
        */
        afterEnter() {
            this.animating = false;
        },
        handleScroll() {
            if (typeof window === 'undefined')
                return;
            if (this.scroll === 'clip') {
                if (this.scrollClass) {
                    if (this.open) {
                        document.documentElement.classList.add(this.scrollClass);
                    }
                    else {
                        document.documentElement.classList.remove(this.scrollClass);
                    }
                    return;
                }
            }
            this.savedScrollTop = !this.savedScrollTop
                ? document.documentElement.scrollTop
                : this.savedScrollTop;
            if (this.scrollClass) {
                if (this.open) {
                    document.body.classList.add(this.scrollClass);
                }
                else {
                    document.body.classList.remove(this.scrollClass);
                }
            }
            if (this.open) {
                document.body.style.top = `-${this.savedScrollTop}px`;
                return;
            }
            document.documentElement.scrollTop = this.savedScrollTop;
            document.body.style.top = null;
            this.savedScrollTop = null;
        }
    },
    created() {
        if (typeof window !== 'undefined') {
            document.addEventListener('keyup', this.keyPress);
            document.addEventListener('click', this.clickedOutside);
        }
    },
    mounted() {
        if (typeof window !== 'undefined') {
            if (this.isFixed) {
                document.body.appendChild(this.$el);
            }
            if (this.overlay && this.open) {
                this.handleScroll();
            }
        }
    },
    beforeUnmount() {
        if (typeof window !== 'undefined') {
            document.removeEventListener('keyup', this.keyPress);
            document.removeEventListener('click', this.clickedOutside);
            if (this.overlay) {
                // reset scroll
                const savedScrollTop = !this.savedScrollTop
                    ? document.documentElement.scrollTop
                    : this.savedScrollTop;
                if (this.scrollClass) {
                    document.body.classList.remove(this.scrollClass);
                    document.documentElement.classList.remove(this.scrollClass);
                }
                document.documentElement.scrollTop = savedScrollTop;
                document.body.style.top = null;
            }
        }
        if (this.isFixed) {
            removeElement(this.$el);
        }
    }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  return withDirectives((openBlock(), createElementBlock("div", {
    class: normalizeClass(_ctx.rootClasses)
  }, [_ctx.overlay && _ctx.isOpen ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass(_ctx.overlayClasses)
  }, null, 2 /* CLASS */)) : createCommentVNode("v-if", true), createVNode(Transition, {
    name: _ctx.transitionName,
    onBeforeEnter: _ctx.beforeEnter,
    onAfterEnter: _ctx.afterEnter,
    persisted: ""
  }, {
    default: withCtx(() => [withDirectives(createElementVNode("div", {
      ref: "sidebarContent",
      class: normalizeClass(_ctx.contentClasses)
    }, [renderSlot(_ctx.$slots, "default")], 2 /* CLASS */), [[vShow, _ctx.isOpen]])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["name", "onBeforeEnter", "onAfterEnter"])], 2 /* CLASS */)), [[vShow, !_ctx.hideOnMobile]]);
}

script.render = render;
script.__file = "src/components/sidebar/Sidebar.vue";

var index = {
    install(app) {
        registerComponent(app, script);
    }
};

export { script as OSidebar, index as default };
